// src/constants/app.js
// ===== APPLICATION CONSTANTS =====

// Environment variables with fallbacks
export const APP_CONFIG = {
  // Basic App Info
  APP_NAME: (process.env.REACT_APP_BRAND_NAME || "Mokshiq") + " Merchant Panel",
  APP_VERSION: "1.0.0",
  APP_DESCRIPTION: "Enterprise React app with RBAC and RTK Query",

  // API Configuration
  API_BASE_URL: "https://merchant.racklepe.com",
  API_VERSION: process.env.REACT_APP_API_VERSION || "v1",
  API_TIMEOUT: parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000,

  // Environment
  NODE_ENV: process.env.NODE_ENV || "development",
  IS_DEVELOPMENT: process.env.NODE_ENV === "development",
  IS_PRODUCTION: process.env.NODE_ENV === "production",
  IS_TESTING: process.env.NODE_ENV === "test",

  // Authentication
  TOKEN_KEY: process.env.REACT_APP_TOKEN_KEY || "auth_token",
  REFRESH_TOKEN_KEY: process.env.REACT_APP_REFRESH_TOKEN_KEY || "refresh_token",
  USER_KEY: process.env.REACT_APP_USER_KEY || "user_data",
  SESSION_TIMEOUT:
    parseInt(process.env.REACT_APP_SESSION_TIMEOUT) || 60 * 60 * 1000, // 1 hour

  // UI Configuration
  ITEMS_PER_PAGE: parseInt(process.env.REACT_APP_ITEMS_PER_PAGE) || 10,
  TOAST_DURATION: parseInt(process.env.REACT_APP_TOAST_DURATION) || 5000,
  DEFAULT_THEME: process.env.REACT_APP_DEFAULT_THEME || "light",
  SIDEBAR_COLLAPSED_KEY: "sidebar_collapsed",

  // Polling Configuration
  DEFAULT_POLLING_INTERVAL:
    parseInt(process.env.REACT_APP_DEFAULT_POLLING_INTERVAL) || 30000,
  DASHBOARD_POLLING_INTERVAL:
    parseInt(process.env.REACT_APP_DASHBOARD_POLLING_INTERVAL) || 15000,
  ACTIVITY_POLLING_INTERVAL:
    parseInt(process.env.REACT_APP_ACTIVITY_POLLING_INTERVAL) || 15000,
  HEALTH_POLLING_INTERVAL:
    parseInt(process.env.REACT_APP_HEALTH_POLLING_INTERVAL) || 10000,

  // Cache Configuration
  DEFAULT_CACHE_TTL: parseInt(process.env.REACT_APP_DEFAULT_CACHE_TTL) || 300, // 5 minutes
  USER_CACHE_TTL: parseInt(process.env.REACT_APP_USER_CACHE_TTL) || 600, // 10 minutes
  DASHBOARD_CACHE_TTL:
    parseInt(process.env.REACT_APP_DASHBOARD_CACHE_TTL) || 120, // 2 minutes
  SETTINGS_CACHE_TTL: parseInt(process.env.REACT_APP_SETTINGS_CACHE_TTL) || 900, // 15 minutes

  // Feature Flags
  ENABLE_DEV_TOOLS: process.env.REACT_APP_ENABLE_DEV_TOOLS === "true",
  ENABLE_ANALYTICS: process.env.REACT_APP_ENABLE_ANALYTICS !== "false",
  ENABLE_SERVICE_MONITORING:
    process.env.REACT_APP_ENABLE_SERVICE_MONITORING !== "false",
  ENABLE_REAL_TIME_UPDATES:
    process.env.REACT_APP_ENABLE_REAL_TIME_UPDATES !== "false",
  ENABLE_OFFLINE_SUPPORT:
    process.env.REACT_APP_ENABLE_OFFLINE_SUPPORT === "true",

  // Development Tools
  REDUX_DEVTOOLS: process.env.REACT_APP_REDUX_DEVTOOLS !== "false",
  PERFORMANCE_MONITORING:
    process.env.REACT_APP_PERFORMANCE_MONITORING === "true",
  DEBUG_MODE: process.env.REACT_APP_DEBUG_MODE === "true",

  // WhiteLabel settings
  ORGANISATION_LOGO: process.env.REACT_APP_LOGO_URL || "/assets/images/logo/2.png",
  ORGANISATION_BRAND: process.env.REACT_APP_BRAND_NAME || "Mokshiq",
  
  // File Upload
  MAX_FILE_SIZE:
    parseInt(process.env.REACT_APP_MAX_FILE_SIZE) || 10 * 1024 * 1024, // 10MB
  ALLOWED_FILE_TYPES: process.env.REACT_APP_ALLOWED_FILE_TYPES?.split(",") || [
    "image/jpeg",
    "image/png",
    "image/gif",
    "application/pdf",
    "text/csv",
    "application/vnd.ms-excel",
  ],

  // Rate Limiting
  DEFAULT_RATE_LIMIT: {
    requests:
      parseInt(process.env.REACT_APP_DEFAULT_RATE_LIMIT_REQUESTS) || 100,
    window: process.env.REACT_APP_DEFAULT_RATE_LIMIT_WINDOW || "1h",
  },

  // WebSocket Configuration (if needed)
  WS_BASE_URL: process.env.REACT_APP_WS_BASE_URL || "ws://localhost:3001",
  WS_RECONNECT_INTERVAL:
    parseInt(process.env.REACT_APP_WS_RECONNECT_INTERVAL) || 5000,
  WS_MAX_RECONNECT_ATTEMPTS:
    parseInt(process.env.REACT_APP_WS_MAX_RECONNECT_ATTEMPTS) || 10,
  DMT_VERSION: "dmt3",
};

// ===== HTTP STATUS CODES =====
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  VALIDATION_ERROR: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504,
};

// ===== NOTIFICATION TYPES =====
export const NOTIFICATION_TYPES = {
  SUCCESS: "success",
  ERROR: "error",
  WARNING: "warning",
  INFO: "info",
};

// ===== TOAST POSITIONS =====
export const TOAST_POSITIONS = {
  TOP_RIGHT: "top-end",
  TOP_LEFT: "top-start",
  BOTTOM_RIGHT: "bottom-end",
  BOTTOM_LEFT: "bottom-start",
  TOP_CENTER: "top-center",
  BOTTOM_CENTER: "bottom-center",
};

// ===== THEME CONFIGURATION =====
export const THEMES = {
  LIGHT: "light",
  DARK: "dark",
  AUTO: "auto",
};

// ===== LOADING STATES =====
export const LOADING_STATES = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
};

// ===== SORT DIRECTIONS =====
export const SORT_DIRECTIONS = {
  ASC: "asc",
  DESC: "desc",
};

// ===== DATE FORMATS =====
export const DATE_FORMATS = {
  SHORT: "MM/DD/YYYY",
  MEDIUM: "MMM DD, YYYY",
  LONG: "MMMM DD, YYYY",
  FULL: "dddd, MMMM DD, YYYY",
  ISO: "YYYY-MM-DDTHH:mm:ssZ",
  TIME_12H: "hh:mm A",
  TIME_24H: "HH:mm",
  DATETIME_SHORT: "MM/DD/YYYY hh:mm A",
  DATETIME_LONG: "MMMM DD, YYYY hh:mm A",
};

// ===== PAGINATION DEFAULTS =====
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  MAX_LIMIT: 100,
  LIMITS: [5, 10, 25, 50, 100],
};

// ===== VALIDATION PATTERNS =====
export const VALIDATION_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^\+?[\d\s\-()]{10,}$/,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/,
  USERNAME: /^[a-zA-Z0-9_]{3,20}$/,
  URL: /^https?:\/\/([da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/,
  IPV4: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
  SLUG: /^[a-z0-9-]+$/,
  HEX_COLOR: /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
};

// ===== FILE CONFIGURATION =====
export const FILE_CONFIG = {
  MAX_SIZE: APP_CONFIG.MAX_FILE_SIZE,
  ALLOWED_TYPES: APP_CONFIG.ALLOWED_FILE_TYPES,
  UPLOAD_CHUNK_SIZE: 1024 * 1024, // 1MB chunks
  IMAGE_TYPES: ["image/jpeg", "image/png", "image/gif", "image/webp"],
  DOCUMENT_TYPES: ["application/pdf", "application/msword", "text/plain"],
  SPREADSHEET_TYPES: [
    "text/csv",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ],
};

// ===== ERROR CODES =====
export const ERROR_CODES = {
  NETWORK_ERROR: "NETWORK_ERROR",
  TIMEOUT_ERROR: "TIMEOUT_ERROR",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  AUTHORIZATION_ERROR: "AUTHORIZATION_ERROR",
  PERMISSION_ERROR: "PERMISSION_ERROR",
  NOT_FOUND_ERROR: "NOT_FOUND_ERROR",
  CONFLICT_ERROR: "CONFLICT_ERROR",
  RATE_LIMIT_ERROR: "RATE_LIMIT_ERROR",
  SERVER_ERROR: "SERVER_ERROR",
  UNKNOWN_ERROR: "UNKNOWN_ERROR",
};

// ===== CACHE KEYS =====
export const CACHE_KEYS = {
  USER_PREFERENCES: "user_preferences",
  THEME: "app_theme",
  SIDEBAR_STATE: "sidebar_state",
  LAST_ROUTE: "last_route",
  FORM_DATA: "form_data",
  SEARCH_HISTORY: "search_history",
  NOTIFICATION_SETTINGS: "notification_settings",
};

// ===== BREAKPOINTS (Bootstrap 5) =====
export const BREAKPOINTS = {
  XS: 0,
  SM: 576,
  MD: 768,
  LG: 992,
  XL: 1200,
  XXL: 1400,
};

// ===== ANIMATION DURATIONS =====
export const ANIMATIONS = {
  FAST: 150,
  NORMAL: 300,
  SLOW: 500,
  EXTRA_SLOW: 1000,
};

// ===== COMPONENT SIZES =====
export const SIZES = {
  SMALL: "sm",
  MEDIUM: "md",
  LARGE: "lg",
  EXTRA_LARGE: "xl",
};

// ===== PRIORITY LEVELS =====
export const PRIORITY_LEVELS = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  URGENT: "urgent",
};

// ===== STATUS TYPES =====
export const STATUS_TYPES = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  PENDING: "pending",
  SUSPENDED: "suspended",
  DELETED: "deleted",
  DRAFT: "draft",
  PUBLISHED: "published",
  ARCHIVED: "archived",
};

// ===== EXPORT FORMATS =====
export const EXPORT_FORMATS = {
  CSV: "csv",
  EXCEL: "xlsx",
  PDF: "pdf",
  JSON: "json",
  XML: "xml",
};

// ===== LOG LEVELS =====
export const LOG_LEVELS = {
  ERROR: "error",
  WARN: "warn",
  INFO: "info",
  DEBUG: "debug",
  TRACE: "trace",
};

// ===== WEBSOCKET EVENTS =====
export const WS_EVENTS = {
  CONNECTION: "connection",
  DISCONNECT: "disconnect",
  ERROR: "error",
  USER_ONLINE: "user_online",
  USER_OFFLINE: "user_offline",
  NOTIFICATION: "notification",
  REAL_TIME_UPDATE: "real_time_update",
  SYSTEM_ALERT: "system_alert",
};

// ===== UTILITY FUNCTIONS =====
export const getConfig = (key, defaultValue = null) => {
  return APP_CONFIG[key] ?? defaultValue;
};

export const isFeatureEnabled = (feature) => {
  return APP_CONFIG[`ENABLE_${feature.toUpperCase()}`] === true;
};

export const getFileTypeFromMime = (mimeType) => {
  const typeMap = {
    "image/": "image",
    "video/": "video",
    "audio/": "audio",
    "application/pdf": "pdf",
    "text/": "text",
    "application/zip": "archive",
    "application/x-rar-compressed": "archive",
  };

  for (const [key, value] of Object.entries(typeMap)) {
    if (mimeType.startsWith(key) || mimeType === key) {
      return value;
    }
  }

  return "file";
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export const MONEY_TRANSFER_ACTION = {
  SEARCH_CUSTOMER: "soft_validate_customer",
  ADD_CUSTOMER: "AddCustomer",
  FETCH_BENE: "soft_fetch_bene",
  FETCH_BANK:"is_bank_list",
  VERIF_BENE: "soft_dmt_ekyc",
  VALIDATE_OTP:"soft_validate_otp",
  ADD_BENE: "soft_add_bene",
  TRANSFER_MONEY: "soft_transaction_dmt",
  TRANSFER_OTP:"soft_transaction_validate_otp"
};
export const MONEY_TRANSFER_ACTION_AIRTEL = {
  SEARCH_CUSTOMER: "money_transfer_action",
  ADD_CUSTOMER: "AddCustomer",
  FETCH_BENE: "soft_fetch_bene",
  FETCH_BANK:"is_bank_list",
  VERIF_BENE: "soft_dmt_ekyc",
  VALIDATE_OTP:"soft_validate_otp",
  ADD_BENE: "soft_add_bene",
  TRANSFER_MONEY: "soft_transaction_dmt",
  TRANSFER_OTP:"soft_transaction_validate_otp"
};

export const DASHBOARD_URL = {
  'Retailer': '/retailer',
  'Main Admin':'/admin_dashboard',
  'Admin':'/admin_dashboard',
  'White label':'/white_label',
  'Master Distributor':'/master_distributor',
  'Distributer':'/distributor',
  'API Partner':'/api_partner',
  'Pancard Agent':'/pancard_user',
  "Platform User": "/platform_user_dashboard", 
  "Payout API": "/payout_api_dashboard",
  "payout_api": "/payout_api_dashboard",
  "Payout": "/payout_api_dashboard",
}
// Export default config for easy access
export default APP_CONFIG;
